/**
 * PredimPage — predimensionamiento de elementos estructurales.
 *
 * Layout (estilo hub colapsable tipo "Icofesa"):
 *   Left rail  : accordion con las 5 familias (Columnas, Vigas, Losas, Muros,
 *                Zapatas). Cada familia lista sus subtipos.
 *   Main       : calculadora del subtipo seleccionado. Si nada está
 *                seleccionado, muestra una pantalla de bienvenida.
 *
 * El predimensionamiento es solo un dimensionamiento PRELIMINAR a partir de
 * fórmulas empíricas / criterios de norma — no reemplaza al diseño detallado
 * que ya está en los otros módulos (Columnas, Vigas, etc).
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronRight, ChevronDown, Hammer, Columns3, Square, Layers, Frame } from 'lucide-react';
import { FootingIcon } from '@/components/icons/FootingIcon';

import { PredimColumn } from './predim/PredimColumn';
import { PredimBeam } from './predim/PredimBeam';
import { PredimSlab } from './predim/PredimSlab';
import { PredimWall } from './predim/PredimWall';
import { PredimFooting } from './predim/PredimFooting';
import { useDesign } from '@/features/designs/useDesigns';
import type { DesignFull } from '@/features/designs/types';

interface SubItem {
  id: string;
  label: string;
}
interface Family {
  id: 'columns' | 'beams' | 'slabs' | 'walls' | 'footings';
  label: string;
  icon: typeof Hammer;
  iconCustom?: () => JSX.Element;
  items: SubItem[];
}

const FAMILIES: Family[] = [
  {
    id: 'columns',
    label: 'Columnas',
    icon: Columns3,
    items: [
      { id: 'columns:central', label: 'Columna central' },
      { id: 'columns:perimeter', label: 'Columna perimetral' },
      { id: 'columns:corner', label: 'Columna esquinera' },
      { id: 'columns:bridge', label: 'Columna de puente' },
    ],
  },
  {
    id: 'beams',
    label: 'Vigas',
    icon: Square,
    items: [
      { id: 'beams:peraltada', label: 'Viga peraltada' },
      { id: 'beams:chata', label: 'Viga chata' },
      { id: 'beams:solera', label: 'Viga solera (albañilería)' },
      { id: 'beams:invertida', label: 'Viga invertida' },
      { id: 'beams:arriostre', label: 'Viga de arriostre' },
    ],
  },
  {
    id: 'slabs',
    label: 'Losas',
    icon: Layers,
    items: [
      { id: 'slabs:aligerada_1d', label: 'Aligerada en una dirección' },
      { id: 'slabs:maciza_1d', label: 'Maciza en una dirección' },
      { id: 'slabs:aligerada_2d', label: 'Aligerada en dos direcciones' },
      { id: 'slabs:maciza_2d', label: 'Maciza en dos direcciones' },
    ],
  },
  {
    id: 'walls',
    label: 'Muros',
    icon: Frame,
    items: [
      { id: 'walls:placa', label: 'Placa de concreto armado' },
    ],
  },
  {
    id: 'footings',
    label: 'Zapatas',
    icon: Hammer,
    iconCustom: () => <FootingIcon size={16} />,
    items: [
      { id: 'footings:isolated', label: 'Zapata aislada' },
      { id: 'footings:strip', label: 'Zapata corrida' },
      { id: 'footings:combined', label: 'Zapata combinada' },
      { id: 'footings:connected', label: 'Zapata conectada' },
    ],
  },
];

const FAMILY_TOTAL_ITEMS = FAMILIES.reduce((acc, f) => acc + f.items.length, 0);

export function PredimPage() {
  const [expanded, setExpanded] = useState<Set<Family['id']>>(new Set(['columns']));
  const [selected, setSelected] = useState<string | null>(null);

  // URL hydration: /app/predim/{id} loads a saved predim and auto-selects
  // the right family+variant in the accordion. The design's subtype is
  // stored as "family:variant" (e.g. "columns:central").
  const params = useParams<{ id?: string }>();
  const designId =
    params.id && params.id !== 'new' && /^\d+$/.test(params.id) ? Number(params.id) : null;
  const designQuery = useDesign(designId);

  useEffect(() => {
    const d = designQuery.data;
    if (!d) return;
    const subtype = d.subtype ?? '';
    if (subtype.includes(':')) {
      const [fam] = subtype.split(':');
      setSelected(subtype);
      // Auto-expand the family so the user sees the active item highlighted.
      setExpanded((s) => new Set([...s, fam as Family['id']]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designQuery.data?.id]);

  const toggle = (id: Family['id']) => {
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-14 shrink-0 items-center gap-6 border-b px-6"
        style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
            Software E.060
          </div>
          <div className="text-sm font-semibold leading-none" style={{ color: 'var(--text-primary)' }}>
            Predimensionamiento
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* LEFT — Accordion list */}
        <aside
          className="flex w-[340px] shrink-0 flex-col overflow-y-auto border-r"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
        >
          {/* Hub-style header card */}
          <div className="border-b p-4" style={{ borderColor: 'var(--border-subtle)' }}>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-[var(--radius-sm)]"
                style={{ background: 'rgba(245,158,11,0.10)', color: 'var(--accent)' }}>
                <Hammer size={18} strokeWidth={1.6} />
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Predimensionamiento
                </div>
                <div className="mt-0.5 inline-flex items-center gap-2 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                  <span className="rounded-full px-2 py-0.5 text-[9.5px] font-semibold"
                    style={{ background: 'rgba(59,130,246,0.15)', color: 'var(--brand-alt, #3B82F6)' }}>
                    Hub
                  </span>
                  <span>{FAMILY_TOTAL_ITEMS} elementos · {FAMILIES.length} familias</span>
                </div>
              </div>
            </div>
          </div>

          {/* Accordion list */}
          <ul className="flex-1 overflow-y-auto py-1">
            {FAMILIES.map((fam) => {
              const isOpen = expanded.has(fam.id);
              return (
                <li key={fam.id}>
                  <button
                    type="button"
                    onClick={() => toggle(fam.id)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-[var(--surface-2)]"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {isOpen ? (
                      <ChevronDown size={14} strokeWidth={2} style={{ color: 'var(--text-muted)' }} />
                    ) : (
                      <ChevronRight size={14} strokeWidth={2} style={{ color: 'var(--text-muted)' }} />
                    )}
                    <span style={{ color: 'var(--accent)' }}>
                      {fam.iconCustom ? fam.iconCustom() : <fam.icon size={16} strokeWidth={1.6} />}
                    </span>
                    <span className="flex-1 font-medium">{fam.label}</span>
                    <span
                      className="rounded-full px-2 py-0.5 text-[10px] font-medium"
                      style={{
                        background: 'var(--surface-2)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      {fam.items.length}
                    </span>
                  </button>
                  {isOpen && (
                    <ul className="border-l ml-[19px] mr-2 mb-1" style={{ borderColor: 'var(--border-subtle)' }}>
                      {fam.items.map((it) => {
                        const isSelected = selected === it.id;
                        return (
                          <li key={it.id}>
                            <button
                              type="button"
                              onClick={() => setSelected(it.id)}
                              className="flex w-full items-center gap-2 py-1.5 pl-3 pr-2 text-left text-[12.5px] transition-colors"
                              style={{
                                background: isSelected ? 'var(--surface-2)' : 'transparent',
                                color: isSelected ? 'var(--accent)' : 'var(--text-secondary)',
                                fontWeight: isSelected ? 600 : 400,
                              }}
                            >
                              <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                                ─
                              </span>
                              <span className="flex-1">{it.label}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </aside>

        {/* CENTER — Predim calculator */}
        <main className="flex-1 overflow-auto p-6" style={{ background: 'var(--surface-canvas)' }}>
          {!selected ? (
            <WelcomeScreen />
          ) : (
            <PredimContent
              itemId={selected}
              // Only feed the design through if the loaded one matches the
              // selected item — switching to a different variant should give
              // the user a fresh form, not stale inputs from another type.
              loadedDesign={
                designQuery.data && designQuery.data.subtype === selected ? designQuery.data : null
              }
            />
          )}
        </main>
      </div>
    </div>
  );
}

function WelcomeScreen() {
  return (
    <div className="mx-auto max-w-2xl py-12 text-center">
      <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full"
        style={{ background: 'rgba(245,158,11,0.10)', color: 'var(--accent)' }}>
        <Hammer size={28} strokeWidth={1.6} />
      </div>
      <h1 className="mb-3 text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
        Predimensionamiento estructural
      </h1>
      <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
        Selecciona un elemento del listado de la izquierda para obtener su predimensionamiento.
        Los valores que se calculan aquí son <strong>preliminares</strong> — un punto de partida
        para luego refinar el diseño en los módulos de cálculo correspondientes (Columnas, Vigas, etc).
      </p>
      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {[
          { icon: <Columns3 size={20} />, label: 'Columnas', desc: 'Sección por carga axial' },
          { icon: <Square size={20} />, label: 'Vigas', desc: 'Peralte por luz' },
          { icon: <Layers size={20} />, label: 'Losas', desc: 'Espesor por luz' },
          { icon: <Frame size={20} />, label: 'Muros', desc: 'Espesor por altura' },
        ].map((c) => (
          <div key={c.label}
            className="rounded-[var(--radius-sm)] border p-3 text-left"
            style={{ borderColor: 'var(--border-default)', background: 'var(--surface-1)' }}>
            <div className="flex items-center gap-2 text-[13px] font-medium" style={{ color: 'var(--text-primary)' }}>
              <span style={{ color: 'var(--accent)' }}>{c.icon}</span>
              {c.label}
            </div>
            <div className="mt-1 text-[11px]" style={{ color: 'var(--text-muted)' }}>{c.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PredimContent({
  itemId,
  loadedDesign,
}: {
  itemId: string;
  loadedDesign: DesignFull | null;
}) {
  const [family, sub] = itemId.split(':');
  // Use a key bound to the design id so React remounts the sub-form when
  // a new design is loaded — preserves the "initial state from props"
  // behavior cleanly without manual reset effects.
  const key = `${itemId}:${loadedDesign?.id ?? 'new'}`;
  // Payload shape: { family, variant, inputs: { ... } }
  const initialInputs = (loadedDesign?.payload as { inputs?: Record<string, unknown> } | undefined)
    ?.inputs;

  switch (family) {
    case 'columns':
      return (
        <PredimColumn
          key={key}
          variant={sub as 'central' | 'perimeter' | 'corner' | 'bridge'}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          initialInputs={initialInputs as any}
          existing={loadedDesign}
        />
      );
    case 'beams':
      return (
        <PredimBeam
          key={key}
          variant={sub as 'peraltada' | 'chata' | 'solera' | 'invertida' | 'arriostre'}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          initialInputs={initialInputs as any}
          existing={loadedDesign}
        />
      );
    case 'slabs':
      return (
        <PredimSlab
          key={key}
          variant={sub as 'aligerada_1d' | 'maciza_1d' | 'aligerada_2d' | 'maciza_2d'}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          initialInputs={initialInputs as any}
          existing={loadedDesign}
        />
      );
    case 'walls':
      return (
        <PredimWall
          key={key}
          variant={sub as 'placa'}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          initialInputs={initialInputs as any}
          existing={loadedDesign}
        />
      );
    case 'footings':
      return (
        <PredimFooting
          key={key}
          variant={sub as 'isolated' | 'strip' | 'combined' | 'connected'}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          initialInputs={initialInputs as any}
          existing={loadedDesign}
        />
      );
    default:
      return <div>Elemento no soportado: {itemId}</div>;
  }
}
